import createClient from 'openapi-fetch';
import type { paths } from './schema';
import type { TokenManager } from './tokens';

export type ApiClient = ReturnType<typeof createApiClient>;

const ANONYMOUS_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/login-investor',
  '/api/v1/auth/refresh',
  '/api/v1/auth/logout',
];

/**
 * The typed REST client (types generated from docs/openapi.json). It attaches the in-memory access token, and when the
 * server answers 401 it renews the token once and repeats the request; if that fails the session is marked expired.
 */
export function createApiClient(
  tokens: TokenManager,
  options: { baseUrl?: string; fetch?: typeof fetch } = {},
) {
  const baseFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  const authFetch = async (request: Request): Promise<Response> => {
    const url = new URL(request.url, 'http://local');
    if (ANONYMOUS_PATHS.includes(url.pathname)) {
      return baseFetch(request);
    }
    const retry = request.clone(); // the body is consumed by the first attempt
    const token = await tokens.getToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    const response = await baseFetch(request);
    if (response.status !== 401 || !token) {
      return response;
    }
    if (await tokens.refresh(token)) {
      retry.headers.set('Authorization', `Bearer ${tokens.accessToken ?? ''}`);
      const second = await baseFetch(retry);
      if (second.status === 401) {
        tokens.markExpired();
      }
      return second;
    }
    return response; // refresh() already recorded why the session ended
  };

  return createClient<paths>({ baseUrl: options.baseUrl ?? '', fetch: authFetch, credentials: 'include' });
}
